import React, { useState } from 'react'
import { Box, Typography, Button, LinearProgress } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { MultiTableConfigTable, MultiTableAnalysis } from '../../../../types/gotypes'
import { API_BASE_URL } from '../../../../constants/system'
import { extractErrorMessage } from '../../../../utils/apitools'
import useDashboardMultiTable from '../../../../hooks/useDashboardMultiTable'
import { WizardStep0Welcome } from './WizardStep0Welcome'
import { WizardStep1Sources } from './WizardStep1Sources'
import { WizardStep2Instructions } from './WizardStep2Instructions'
import { WizardStep3Preview, PreviewState } from './WizardStep3Preview'
import { applyGuidedToggles, buildExampleScaffold, tableFullPath, GuidedToggleState } from './wizardHelpers'

export const GENAI_WIZARD_PERSONA = 'Executive Summarizer'

export interface GenAIWizardCompleteResult {
  title: string
  tables: MultiTableConfigTable[]
  instructions: string
  /** Structured guided-toggle state behind `instructions`' trailing guidance
   * sentence (#1047) — carried into the saved widget data so the toggles
   * re-derive correctly the first time the flat editor reopens this card. */
  guidedToggles: GuidedToggleState
  /** Set once the wizard has created a live multi-table (real dashboardId
   * case) — lets the caller carry it straight into the save instead of
   * letting the generic create-on-save path run (and re-trigger analysis) a
   * second time. */
  multiTableId?: string
}

export interface GenAIWizardProps {
  customer: string
  job: string
  /** The dashboard this card is being added to. `'new'` (or empty) means the
   * dashboard itself hasn't been saved yet, so there's no id to create a live
   * multi-table against — the live preview step is deferred until after save. */
  dashboardId: string
  /** #1810: the page's filter map, sent with the preview run so the card it
   *  becomes was generated under the filters it renders under. */
  jobFilters?: Record<string, string>
  onComplete: (result: GenAIWizardCompleteResult) => void
  onCancel: () => void
}

const STEP_LABELS = [
  'Step 0 of 3 · Getting started',
  'Step 1 of 3 · Data sources',
  'Step 2 of 3 · Instructions',
  'Step 3 of 3 · Preview & refine',
]

/**
 * 4-step guided creation wizard for a first-time Gen AI Insights card (#970
 * Part A). Only shown when the card has no `multi_table_id` yet — once a card
 * is configured, the ⚙️ settings cog reopens the flat edit modal instead
 * (`MultiTableAnalysisWidgetEditor`, Part B).
 */
export const GenAIWizard: React.FC<GenAIWizardProps> = ({ customer, job, dashboardId, jobFilters, onComplete, onCancel }) => {
  const realDashboardId = dashboardId && dashboardId !== 'new' ? dashboardId : ''

  const [step, setStep] = useState(0)
  const [title, setTitle] = useState('')
  const [tables, setTables] = useState<MultiTableConfigTable[]>([])
  const [instructions, setInstructions] = useState('')
  const [guidedToggles, setGuidedToggles] = useState<GuidedToggleState>({})
  const [scaffolded, setScaffolded] = useState(false)
  const [multiTableId, setMultiTableId] = useState<string | undefined>(undefined)
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const [genRefused, setGenRefused] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const { createMultiTable, updateMultiTable } = useDashboardMultiTable({
    customer,
    dashboardId: realDashboardId,
  })

  const analysesQueryKey = ['multi_table_analyses', { customer, job, multiTableId }]
  const analysesQuery = useQuery({
    queryKey: analysesQueryKey,
    enabled: Boolean(customer && job && multiTableId),
    refetchInterval: (query) => {
      const data = query.state.data as MultiTableAnalysis[] | undefined
      if (data?.some(a => a.state === 'running' || a.state === 'pending')) return 2000
      if (data && data.length === 0) return 3000
      return false
    },
    queryFn: async () => {
      const res = await axios.get<{ analyses: MultiTableAnalysis[] }>(
        `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis`
      )
      return res.data.analyses || []
    },
  })

  const analyses = analysesQuery.data || []
  const completedAnalysis = analyses.find(a => a.persona === GENAI_WIZARD_PERSONA && a.state === 'completed')
  const runningAnalysis = analyses.some(a => a.state === 'running' || a.state === 'pending')
  const failedOnly = !completedAnalysis && analyses.some(a => a.persona === GENAI_WIZARD_PERSONA && a.state === 'failed')

  let previewState: PreviewState
  if (!realDashboardId) {
    previewState = { kind: 'deferred' }
  } else if (generating || runningAnalysis) {
    previewState = { kind: 'loading' }
  } else if (genRefused) {
    previewState = { kind: 'refused', message: genRefused }
  } else if (genError) {
    previewState = { kind: 'error', message: genError }
  } else if (completedAnalysis) {
    previewState = { kind: 'ready', report: completedAnalysis.combined_report || '' }
  } else if (failedOnly) {
    previewState = { kind: 'error', message: 'Analysis failed.' }
  } else {
    previewState = { kind: 'idle' }
  }

  const goToStep2 = () => {
    if (!scaffolded && !instructions.trim()) {
      setInstructions(buildExampleScaffold())
      setScaffolded(true)
    }
    setStep(2)
  }

  const handleGeneratePreview = async () => {
    setStep(3)
    if (!realDashboardId) return
    setGenerating(true)
    setGenError(null)
    setGenRefused(null)
    try {
      let mtId = multiTableId
      if (!mtId) {
        const created = await createMultiTable({ tables, name: title || 'Dashboard Analysis' })
        mtId = created.id
        setMultiTableId(mtId)
      } else {
        await updateMultiTable({ id: mtId, config: { tables, name: title || 'Dashboard Analysis' } })
      }
      await axios.post(`${API_BASE_URL}/multitables/${customer}/${job}/${mtId}/analysis/start`, {
        persona: GENAI_WIZARD_PERSONA,
        analysis_instructions: instructions,
        // The preview must honour the format/length/audience toggles in its
        // visible (synthesis) output too, not just per-table analyses.
        synthesis_instructions: applyGuidedToggles('', guidedToggles) || undefined,
        force: true,
        job_filters: jobFilters,
      })
      queryClient.invalidateQueries({ queryKey: ['multi_table_analyses', { customer, job, multiTableId: mtId }] })
    } catch (e) {
      // #1105: a permission refusal is not a failed preview. Keep it apart so
      // Step 3 explains it, offers no Retry, and disables Accept.
      const status = (e as { response?: { status?: number } } | null)?.response?.status
      if (status === 403) setGenRefused(extractErrorMessage(e))
      else setGenError(extractErrorMessage(e))
    } finally {
      setGenerating(false)
    }
  }

  const handleAccept = () => {
    if (genRefused) return
    onComplete({ title, tables, instructions, guidedToggles, multiTableId })
  }

  const canGoNextFromStep1 = tables.length > 0
  const canGeneratePreview = instructions.trim().length > 0

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {step > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">{STEP_LABELS[step]}</Typography>
          <LinearProgress variant="determinate" value={(step / 3) * 100} sx={{ mt: 0.5, height: 4, borderRadius: 2 }} color="info" />
        </Box>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}>
        {step === 0 && (
          <WizardStep0Welcome title={title} onTitleChange={setTitle} onNext={() => setStep(1)} />
        )}
        {step === 1 && (
          <WizardStep1Sources customer={customer} job={job} tables={tables} onTablesChange={setTables} />
        )}
        {step === 2 && (
          <WizardStep2Instructions
            customer={customer}
            job={job}
            title={title}
            instructions={instructions}
            onInstructionsChange={setInstructions}
            mentionSources={tables.map(t => t.displayName)}
            chatSources={tables.map(t => ({ name: t.displayName || tableFullPath(t), path: tableFullPath(t) }))}
            guidedToggles={guidedToggles}
            onGuidedTogglesChange={setGuidedToggles}
          />
        )}
        {step === 3 && (
          <WizardStep3Preview
            state={previewState}
            onGeneratePreview={handleGeneratePreview}
            onRefine={() => setStep(2)}
            onAccept={handleAccept}
            accepting={false}
          />
        )}
      </Box>

      {step > 0 && step < 3 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setStep(step - 1)}>
            Back
          </Button>
          {step === 1 && (
            <Button variant="contained" color="info" endIcon={<ArrowForwardIcon />} disabled={!canGoNextFromStep1} onClick={goToStep2}>
              Next
            </Button>
          )}
          {step === 2 && (
            <Button variant="contained" color="info" endIcon={<ArrowForwardIcon />} disabled={!canGeneratePreview} onClick={handleGeneratePreview}>
              Generate preview
            </Button>
          )}
        </Box>
      )}

      {step === 0 && (
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button size="small" color="inherit" onClick={onCancel}>Cancel</Button>
        </Box>
      )}
    </Box>
  )
}

export default GenAIWizard
